import { ProviderDetailsResponse } from '../../types';
import { getCustomerIdFromToken } from '../../utils/getCustomerIdFromToken';
import { get } from '../apiService';

const PROVIDER_GET_TTL_MS = 5000;
const providerGetCache = new Map<
  string,
  {
    timestamp: number;
    result: {
      data: ProviderDetailsResponse | null;
      loading: boolean;
    };
  }
>();
const providerGetInFlight = new Map<
  string,
  Promise<{ data: ProviderDetailsResponse | null; loading: boolean }>
>();

export const invalidateProviderByIdCache = (
  providerId?: string | null,
): void => {
  if (!providerId) {
    providerGetCache.clear();
    providerGetInFlight.clear();
    return;
  }

  for (const key of Array.from(providerGetCache.keys())) {
    if (key.endsWith(`:${providerId}`)) {
      providerGetCache.delete(key);
    }
  }

  for (const key of Array.from(providerGetInFlight.keys())) {
    if (key.endsWith(`:${providerId}`)) {
      providerGetInFlight.delete(key);
    }
  }
};

export const getProviderById = async (
  id?: string | null,
): Promise<{
  data: ProviderDetailsResponse | null;
  loading: boolean;
}> => {
  const customer = getCustomerIdFromToken();
  if (!customer) {
    throw new Error('Customer Id is not available');
  }
  if (!id) {
    throw new Error('Id is not available');
  }

  const cacheKey = `${customer}:${id}`;
  const now = Date.now();
  const cached = providerGetCache.get(cacheKey);
  if (cached) {
    if (now - cached.timestamp < PROVIDER_GET_TTL_MS) {
      return cached.result;
    }
    providerGetCache.delete(cacheKey);
  }

  const inFlight = providerGetInFlight.get(cacheKey);
  if (inFlight) {
    return inFlight;
  }

  const url = `resourceMatrix/providers/get?providerId=${id}&customerId=${customer}&includeRelatedPractices=true`;
  let loading = true;
  const request = (async () => {
    try {
      const responseData = await get(url);
      const data = responseData as ProviderDetailsResponse;
      loading = false;
      const result = { data, loading };
      providerGetCache.set(cacheKey, {
        timestamp: Date.now(),
        result,
      });
      return result;
    } catch (error) {
      loading = false;
      throw new Error('Failed to fetch provider item');
    }
  })();

  providerGetInFlight.set(cacheKey, request);
  try {
    return await request;
  } finally {
    providerGetInFlight.delete(cacheKey);
  }
};
